import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { PropertyCard } from "../components/PropertyCard";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PropertyMap } from "../components/PropertyMap";
import { WA_COUNTRY_NAMES, WA_CITIES } from "../lib/westafrica";
import { MagnifyingGlass, Buildings, MapTrifold, GridFour } from "@phosphor-icons/react";

const MODES = [["", "Tous"], ["sale", "Achat"], ["longterm", "Location longue durée"], ["event", "Location courte durée"]];
const TYPES = [["all", "Tous types"], ["apartment", "Appartement"], ["house", "Maison"], ["villa", "Villa"], ["land", "Terrain"], ["hall", "Salle / Espace"]];

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [mapItems, setMapItems] = useState(null);
  const [f, setF] = useState({ mode: "", q: "", country: "all", city: "all", property_type: "all", min_price: "", max_price: "" });

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (f.mode) params.mode = f.mode;
    if (f.q) params.q = f.q;
    if (f.country !== "all") params.country = f.country;
    if (f.city !== "all") params.city = f.city;
    if (f.property_type && f.property_type !== "all") params.property_type = f.property_type;
    if (f.min_price) params.min_price = f.min_price;
    if (f.max_price) params.max_price = f.max_price;
    const { data } = await api.get("/properties", { params });
    setItems(data);
    setMapItems(null);
    setLoading(false);
  }, [f]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <span className="overline text-xs text-orange-800">Catalogue</span>
        <h1 className="font-display text-4xl font-bold text-stone-900 mt-2">Trouvez votre bien idéal</h1>

        {/* mode tabs */}
        <div className="flex flex-wrap gap-2 mt-6">
          {MODES.map(([v, l]) => (
            <button key={v} data-testid={`filter-mode-${v || "all"}`} onClick={() => setF({ ...f, mode: v })}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${f.mode === v ? "bg-orange-800 text-white border-orange-800" : "bg-white text-stone-600 border-stone-200 hover:border-orange-300"}`}>
              {l}
            </button>
          ))}
        </div>

        {/* filters */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4 mt-4 grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="relative lg:col-span-1">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
            <Input data-testid="filter-search" placeholder="Rechercher…" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} className="pl-9" />
          </div>
          <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v, city: "all" })}>
            <SelectTrigger data-testid="filter-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les pays</SelectItem>
              {WA_COUNTRY_NAMES.map((c) => <SelectItem key={c} value={c} data-testid={`filter-country-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.city} onValueChange={(v) => setF({ ...f, city: v })}>
            <SelectTrigger data-testid="filter-city"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les villes</SelectItem>
              {(f.country !== "all" ? WA_CITIES[f.country] : [...new Set(Object.values(WA_CITIES).flat())].sort()).map((c) => (
                <SelectItem key={c} value={c} data-testid={`filter-city-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={f.property_type} onValueChange={(v) => setF({ ...f, property_type: v })}>
            <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="filter-minprice" type="number" placeholder="Prix min" value={f.min_price} onChange={(e) => setF({ ...f, min_price: e.target.value })} />
          <Input data-testid="filter-maxprice" type="number" placeholder="Prix max" value={f.max_price} onChange={(e) => setF({ ...f, max_price: e.target.value })} />
        </div>

        {/* view toggle */}
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <span className="text-sm text-stone-500" data-testid="catalog-count">{items.length} bien(s)</span>
          <div className="flex gap-1 bg-white border border-stone-200 rounded-full p-1">
            <button data-testid="view-grid" onClick={() => setView("grid")} className={`flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full transition-colors ${view === "grid" ? "bg-orange-800 text-white" : "text-stone-500"}`}>
              <GridFour size={16} /> Grille
            </button>
            <button data-testid="view-map" onClick={() => setView("map")} className={`flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full transition-colors ${view === "map" ? "bg-orange-800 text-white" : "text-stone-500"}`}>
              <MapTrifold size={16} /> Carte
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-24 text-center text-stone-400">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="py-24 text-center text-stone-400" data-testid="catalog-empty">
            <Buildings size={40} className="mx-auto mb-3 opacity-40" />
            Aucun bien ne correspond à votre recherche.
          </div>
        ) : view === "map" ? (
          <div className="mt-6">
            <PropertyMap properties={mapItems ?? items} height={520} showAreaSearch areaActive={mapItems !== null}
              onReset={() => setMapItems(null)}
              onAreaSearch={(bounds) => {
                const inside = items.filter((p) => p.latitude && p.longitude && bounds.contains([p.latitude, p.longitude]));
                setMapItems(inside);
                toast.info(`${inside.length} bien(s) dans cette zone`);
              }} />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6" data-testid="catalog-grid">
            {items.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
